import React, { useCallback, useEffect, useState } from 'react';
import { storage } from '../../utils/platform';
import { API_BASE } from '../../config/api';
import { useCurrentRestaurant } from '../contexts/CurrentRestaurantContext';
import SubPageHeader from '../components/SubPageHeader';
import SectionCard from '../components/SectionCard';
import EmptyState from '../components/EmptyState';
import LockedNotice from '../components/LockedNotice';

/**
 * MobileConcurrents — classement face aux établissements voisins.
 *
 * ⚠️ Aucune donnée de démonstration ici. L'onglet Concurrents du web affichait
 * un classement inventé (« Chez Fonfon », « La Bouillabaisse »…) dès que la
 * liste réelle était vide — corrigé en juillet 2026. On ne réintroduit pas le
 * problème : sans données, on affiche un état vide honnête.
 */
export default function MobileConcurrents() {
  const { currentRestaurant, currentRestaurantId, capabilities } = useCurrentRestaurant();
  const [data, setData] = useState({ competitors: [], position: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Repli permissif tant que l'API n'a pas répondu : ne pas verrouiller à tort.
  const peutVoir = capabilities ? capabilities.competitors === true : true;
  const placeId = currentRestaurant?.google_place_id;

  const load = useCallback(async () => {
    if (!peutVoir) { setLoading(false); return; }
    if (!placeId) { setLoading(false); return; }
    setLoading(true);
    setError(null);
    try {
      const token = await storage.get('token');
      const res = await fetch(
        `${API_BASE}/api/places/competitors?placeId=${encodeURIComponent(placeId)}&radius=1500&max=5`,
        {
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
            ...(currentRestaurantId ? { 'X-Restaurant-Id': currentRestaurantId } : {}),
          },
        }
      );
      const payload = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(payload?.error || `HTTP ${res.status}`);
      setData({
        competitors: Array.isArray(payload?.competitors) ? payload.competitors : [],
        position: payload?.position ?? 0,
        total: payload?.total ?? 0,
      });
    } catch (e) {
      setError(e.message || 'Chargement impossible');
    } finally {
      setLoading(false);
    }
  }, [placeId, peutVoir, currentRestaurantId]);

  useEffect(() => { load(); }, [load]);

  if (!peutVoir) {
    return (
      <div style={styles.page}>
        <SubPageHeader title="Concurrents" subtitle="Votre position face aux restaurants voisins" />
        <LockedNotice
          emoji="📍"
          title="Suivi des concurrents"
          webText="Comparez votre note et votre position à celles des restaurants autour de vous. Inclus à partir de la formule Pro."
        />
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <SubPageHeader title="Concurrents" subtitle="Votre position face aux restaurants voisins" />

      {loading && <div style={styles.loading}>Analyse du voisinage…</div>}

      {!loading && !placeId && (
        <EmptyState
          emoji="🔗"
          title="Fiche Google non reliée"
          subtitle="Reliez votre fiche Google dans la configuration de votre restaurant pour situer votre établissement parmi ses voisins."
        />
      )}

      {!loading && placeId && error && (
        <EmptyState
          emoji="⚠️"
          title="Analyse indisponible"
          subtitle={String(error)}
          action={<button type="button" style={styles.retry} onClick={load}>Réessayer</button>}
        />
      )}

      {!loading && placeId && !error && data.competitors.length === 0 && (
        <EmptyState
          emoji="📍"
          title="Aucun voisin trouvé"
          subtitle="Nous n'avons pas identifié d'établissement comparable dans un rayon de 1,5 km."
        />
      )}

      {!loading && data.competitors.length > 0 && (
        <>
          {data.position > 0 && (
            <SectionCard style={styles.rankCard}>
              <div style={styles.rankValue}>#{data.position}</div>
              <div style={styles.rankLabel}>
                sur {data.total} établissement{data.total > 1 ? 's' : ''} comparé{data.total > 1 ? 's' : ''}
              </div>
            </SectionCard>
          )}

          {data.competitors.map((c, i) => (
            <SectionCard key={`${c.name}-${i}`} style={{ ...styles.row, ...(c.isYou ? styles.rowYou : null) }}>
              <div style={styles.rowLeft}>
                <div style={styles.rank}>#{c.rank ?? i + 1}</div>
                <div style={styles.info}>
                  <div style={styles.name}>
                    {c.name}
                    {c.isYou && <span style={styles.youTag}>vous</span>}
                  </div>
                  <div style={styles.meta}>
                    {typeof c.reviews === 'number' ? `${c.reviews} avis` : '—'}
                  </div>
                </div>
              </div>
              <div style={styles.rating}>
                {typeof c.rating === 'number' ? c.rating.toFixed(1) : '—'}
                <span style={styles.star} aria-hidden>★</span>
              </div>
            </SectionCard>
          ))}
        </>
      )}
    </div>
  );
}

const styles = {
  page: { padding: '4px 0 12px' },
  loading: { padding: '40px 0', textAlign: 'center', color: '#6B6B6B', fontSize: 14 },
  rankCard: { textAlign: 'center', marginBottom: 12, padding: '18px 16px' },
  rankValue: { fontSize: 34, fontWeight: 800, color: '#E8502A', lineHeight: 1 },
  rankLabel: { fontSize: 13, color: '#6B6B6B', marginTop: 4 },
  row: {
    marginBottom: 8, padding: '12px 14px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
  },
  rowYou: { border: '1.5px solid #E8502A' },
  rowLeft: { display: 'flex', alignItems: 'center', gap: 12, minWidth: 0, flex: 1 },
  rank: { fontSize: 13, fontWeight: 800, color: '#8A867C', minWidth: 26 },
  info: { minWidth: 0 },
  name: {
    fontSize: 14.5, fontWeight: 700, color: '#1A1A1A',
    display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
  },
  youTag: {
    fontSize: 10, fontWeight: 800, textTransform: 'uppercase',
    background: '#E8502A', color: '#FFF', borderRadius: 6, padding: '1px 6px',
  },
  meta: { fontSize: 12, color: '#6B6B6B', marginTop: 2 },
  rating: { fontSize: 17, fontWeight: 800, color: '#1A1A1A', whiteSpace: 'nowrap' },
  star: { color: '#F5A623', marginLeft: 3, fontSize: 15 },
  retry: {
    padding: '10px 18px', borderRadius: 999, border: 'none',
    background: '#E8502A', color: '#FFF', fontFamily: 'inherit',
    fontSize: 14, fontWeight: 700, cursor: 'pointer',
  },
};
